import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Baby, Loader2, School, UserRound, Search, Phone, Mail, Shield, GraduationCap, Crown, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getRoleBasedPath } from "@/hooks/useRoleRedirect";
import { plans } from "@/components/subscription/plans-data";

function phoneToEmail(phone: string): string {
  const cleaned = phone.replace(/\D/g, "");
  return `${cleaned}@phone.nursery.local`;
}

const Auth = () => {
  const navigate = useNavigate();
  const { user, roles, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);

  // Login state
  const [loginMethod, setLoginMethod] = useState<"email" | "phone">("email");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPhone, setLoginPhone] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Signup shared
  const [signupType, setSignupType] = useState<"admin" | "parent">("admin");
  const [signupMethod, setSignupMethod] = useState<"email" | "phone">("email");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [fullName, setFullName] = useState("");

  // Admin signup
  const [nurseryName, setNurseryName] = useState("");
  const [nurseryAddress, setNurseryAddress] = useState("");
  const [nurseryPhone, setNurseryPhone] = useState("");
  const [nurseryTaxNumber, setNurseryTaxNumber] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("basic");
  const [wantTrial, setWantTrial] = useState(true);

  // Parent signup
  const [nurseries, setNurseries] = useState<{ id: string; name: string }[]>([]);
  const [selectedNurseryId, setSelectedNurseryId] = useState("");
  const [nurserySearch, setNurserySearch] = useState("");
  const [loadingNurseries, setLoadingNurseries] = useState(false);

  // Fetch nurseries for parent signup
  useEffect(() => {
    if (signupType === "parent") {
      setLoadingNurseries(true);
      supabase
        .rpc("lookup_nurseries")
        .then(({ data }) => {
          setNurseries((data as { id: string; name: string }[]) || []);
          setLoadingNurseries(false);
        });
    }
  }, [signupType]);

  // State for onboarding recovery (user logged in but no roles)
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Auto-redirect logged-in users based on role
  useEffect(() => {
    if (!authLoading && user) {
      if (roles.length > 0) {
        navigate(getRoleBasedPath(roles), { replace: true });
      } else {
        // User exists but has no roles — show onboarding form
        setNeedsOnboarding(true);
        // Pre-fill name from profile metadata
        const metaName = user.user_metadata?.full_name;
        if (metaName && !fullName) setFullName(metaName);
      }
    }
  }, [authLoading, user, roles, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const email = loginMethod === "phone" ? phoneToEmail(loginPhone) : loginEmail;
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password: loginPassword,
    });
    setLoading(false);
    if (error) {
      const msg = error.message.includes("weak_password") || error.message.includes("pwned")
        ? "كلمة المرور ضعيفة أو مسربة، يرجى اختيار كلمة مرور أقوى"
        : error.message.includes("Invalid login")
        ? "البريد الإلكتروني أو كلمة المرور غير صحيحة"
        : error.message;
      toast.error("خطأ في تسجيل الدخول: " + msg);
    } else {
      toast.success("تم تسجيل الدخول بنجاح!");
      // Redirect handled by useEffect based on roles
    }
  };

  const getSignupEmail = () => {
    return signupMethod === "phone" ? phoneToEmail(signupPhone) : signupEmail;
  };

  const isAlreadyRegisteredError = (message?: string) => {
    const normalized = (message || "").toLowerCase();
    return normalized.includes("already registered") || normalized.includes("user_already_exists");
  };

  const resolveSignupUserId = async (): Promise<{
    userId: string | null;
    usedExistingAccount: boolean;
    errorMessage?: string;
  }> => {
    const email = getSignupEmail();

    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password: signupPassword,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: window.location.origin,
      },
    });

    if (!authError && authData.user) {
      // signUp succeeded but does NOT create a session (even with auto-confirm).
      // We must sign in immediately to establish auth.uid() for the RPC calls.
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: signupPassword,
      });
      if (signInError) {
        return { userId: null, usedExistingAccount: false, errorMessage: "تم إنشاء الحساب لكن فشل تسجيل الدخول التلقائي. حاول تسجيل الدخول يدوياً." };
      }
      return { userId: authData.user.id, usedExistingAccount: false };
    }

    if (isAlreadyRegisteredError(authError?.message)) {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: signupPassword,
      });

      if (signInError || !signInData.user) {
        return {
          userId: null,
          usedExistingAccount: true,
          errorMessage: "هذا البريد مسجل بالفعل. ادخل بكلمة المرور الصحيحة أو استخدم تسجيل الدخول.",
        };
      }

      return { userId: signInData.user.id, usedExistingAccount: true };
    }

    const msg = authError?.message.includes("weak_password") || authError?.message.includes("pwned")
      ? "كلمة المرور ضعيفة أو مسربة، يرجى اختيار كلمة مرور أقوى"
      : authError?.message || "حدث خطأ";

    return { userId: null, usedExistingAccount: false, errorMessage: msg };
  };

  const handleAdminSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nurseryName.trim()) {
      toast.error("يرجى إدخال اسم الحضانة");
      return;
    }
    if (!nurseryAddress.trim()) {
      toast.error("يرجى إدخال عنوان الحضانة");
      return;
    }
    if (!nurseryPhone.trim()) {
      toast.error("يرجى إدخال رقم هاتف الحضانة");
      return;
    }
    setLoading(true);

    let userId: string;
    let usedExisting = false;

    if (needsOnboarding && user) {
      // User is already signed in, just needs nursery setup
      userId = user.id;
      usedExisting = true;
    } else {
      const signupResult = await resolveSignupUserId();
      if (!signupResult.userId) {
        setLoading(false);
        toast.error("خطأ في إنشاء الحساب: " + (signupResult.errorMessage || "حدث خطأ"));
        return;
      }
      userId = signupResult.userId;
      usedExisting = signupResult.usedExistingAccount;
    }

    // Use SECURITY DEFINER function to handle nursery + role + subscription atomically
    const { error: regError } = await supabase.rpc("register_nursery_admin", {
      _user_id: userId,
      _nursery_name: nurseryName,
      _full_name: fullName,
      _phone: signupPhone || null,
      _plan: selectedPlan,
      _want_trial: wantTrial,
      _nursery_address: nurseryAddress,
      _nursery_phone: nurseryPhone,
      _tax_number: nurseryTaxNumber || null,
    } as any);

    if (regError) {
      setLoading(false);
      const regMsg = regError.message.includes("المستخدم مرتبط بحضانة بالفعل")
        ? "هذا الحساب مرتبط بحضانة بالفعل، استخدم تسجيل الدخول مباشرة."
        : regError.message;
      toast.error("خطأ في إنشاء الحضانة: " + regMsg);
      return;
    }

    setLoading(false);
    toast.success(
      (usedExisting ? "تم إكمال تسجيل الحضانة بنجاح!" : "تم إنشاء الحساب والحضانة بنجاح!") +
        (wantTrial ? " تم تفعيل التجربة المجانية لمدة 15 يوم" : "")
    );
    setNeedsOnboarding(false);
    // Navigate directly since roles won't auto-refresh from RPC
    navigate("/dashboard", { replace: true });
  };

  const handleParentSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedNurseryId) {
      toast.error("يرجى اختيار الحضانة");
      return;
    }
    setLoading(true);

    const signupResult = await resolveSignupUserId();

    if (!signupResult.userId) {
      setLoading(false);
      toast.error("خطأ في إنشاء الحساب: " + (signupResult.errorMessage || "حدث خطأ"));
      return;
    }

    const { error: regError } = await supabase.rpc("register_parent", {
      _user_id: signupResult.userId,
      _nursery_id: selectedNurseryId,
      _full_name: fullName,
    });

    if (regError) {
      setLoading(false);
      const regMsg = regError.message.includes("المستخدم مرتبط بحضانة بالفعل")
        ? "هذا الحساب مرتبط مسبقاً بحضانة، استخدم تسجيل الدخول مباشرة."
        : regError.message;
      toast.error("خطأ في التسجيل: " + regMsg);
      return;
    }

    // Save phone if provided
    if (signupPhone) {
      await supabase
        .from("profiles")
        .update({ phone: signupPhone })
        .eq("user_id", signupResult.userId);
    }

    setLoading(false);
    toast.success(signupResult.usedExistingAccount ? "تم إكمال التسجيل كولي أمر بنجاح!" : "تم إنشاء حساب ولي الأمر بنجاح!");
    // Redirect handled by useEffect based on roles
  };

  const filteredNurseries = nurseries.filter((n) =>
    n.name.toLowerCase().includes(nurserySearch.toLowerCase())
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Helmet>
        <title>تسجيل الدخول | NurseryNet</title>
        <meta name="description" content="سجّل الدخول أو أنشئ حساب جديد على NurseryNet — منصة إدارة الحضانات ورياض الأطفال." />
        <link rel="canonical" href="https://nurserynet.cloud/auth" />
        <meta property="og:title" content="تسجيل الدخول | NurseryNet" />
        <meta property="og:description" content="سجّل الدخول أو أنشئ حساب جديد على NurseryNet." />
        <meta property="og:url" content="https://nurserynet.cloud/auth" />
      </Helmet>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl gradient-hero flex items-center justify-center mx-auto mb-3">
            <Baby className="w-7 h-7 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">NurseryNet — تسجيل الدخول</h1>
          <p className="text-muted-foreground text-sm mt-1">منصة إدارة الحضانات</p>
        </div>

        <Card className="shadow-elevated">
          {needsOnboarding ? (
            /* ONBOARDING RECOVERY — user logged in but has no roles */
            <CardContent className="pt-6">
              <div className="text-center mb-4">
                <Badge variant="secondary" className="mb-2">إكمال التسجيل</Badge>
                <p className="text-sm text-muted-foreground">حسابك موجود لكن لم يتم ربطه بحضانة. أكمل البيانات:</p>
              </div>
              <form onSubmit={handleAdminSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label>الاسم الكامل</Label>
                  <Input value={fullName} onChange={(e) => setFullName(e.target.value)} required placeholder="محمد أحمد" />
                </div>
                <div className="space-y-2">
                  <Label>اسم الحضانة *</Label>
                  <Input value={nurseryName} onChange={(e) => setNurseryName(e.target.value)} required placeholder="حضانة النور" />
                </div>
                <div className="space-y-2">
                  <Label>عنوان الحضانة *</Label>
                  <Input value={nurseryAddress} onChange={(e) => setNurseryAddress(e.target.value)} required placeholder="المدينة - الشارع" />
                </div>
                <div className="space-y-2">
                  <Label>هاتف الحضانة *</Label>
                  <Input value={nurseryPhone} onChange={(e) => setNurseryPhone(e.target.value)} required placeholder="0xxxxxxxxx" dir="ltr" className="text-right" />
                </div>
                <div className="space-y-2">
                  <Label>الرقم الضريبي (اختياري)</Label>
                  <Input value={nurseryTaxNumber} onChange={(e) => setNurseryTaxNumber(e.target.value)} placeholder="123-456-789" dir="ltr" className="text-right" />
                </div>
                {/* Plan Selection */}
                <div className="space-y-2">
                  <Label>اختر الخطة</Label>
                  <div className="grid gap-2">
                    {plans.map((plan) => {
                      const isSelected = selectedPlan === plan.id;
                      const PlanIcon = plan.id === "basic" ? Shield : plan.id === "standard" ? GraduationCap : Crown;
                      return (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => setSelectedPlan(plan.id)}
                          className={`relative flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-right ${
                            isSelected ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"
                          }`}
                        >
                          <PlanIcon className={`w-5 h-5 shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{plan.name}</span>
                              {plan.popular && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">الأكثر طلباً</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground">{plan.monthlyPrice} ج.م / شهرياً</p>
                          </div>
                          <div className={`w-4 h-4 rounded-full border-2 shrink-0 ${isSelected ? "border-primary bg-primary" : "border-muted-foreground/40"}`}>
                            {isSelected && <div className="w-1.5 h-1.5 bg-primary-foreground rounded-full m-auto mt-[3px]" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-primary" />
                    <div>
                      <p className="text-sm font-medium">تجربة مجانية 15 يوم</p>
                      <p className="text-xs text-muted-foreground">ابدأ مجاناً بدون أي رسوم</p>
                    </div>
                  </div>
                  <Switch checked={wantTrial} onCheckedChange={setWantTrial} aria-label="تفعيل التجربة المجانية لمدة 15 يوم" />
                </div>
                <Button type="submit" className="w-full gradient-hero border-0" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "إنشاء الحضانة"}
                </Button>
              </form>
            </CardContent>
          ) : (
          <Tabs defaultValue="login" dir="rtl">
            <CardHeader className="pb-2">
              <TabsList className="w-full">
                <TabsTrigger value="login" className="flex-1">تسجيل الدخول</TabsTrigger>
                <TabsTrigger value="signup" className="flex-1">حساب جديد</TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent>
              {/* LOGIN TAB */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  {/* Login method toggle */}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={loginMethod === "email" ? "default" : "outline"}
                      className="flex-1 gap-2"
                      onClick={() => setLoginMethod("email")}
                    >
                      <Mail className="w-4 h-4" />
                      بريد إلكتروني
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={loginMethod === "phone" ? "default" : "outline"}
                      className="flex-1 gap-2"
                      onClick={() => setLoginMethod("phone")}
                    >
                      <Phone className="w-4 h-4" />
                      رقم الهاتف
                    </Button>
                  </div>

                  {loginMethod === "email" ? (
                    <div className="space-y-2">
                      <Label>البريد الإلكتروني</Label>
                      <Input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required placeholder="email@example.com" dir="ltr" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>رقم الهاتف</Label>
                      <Input type="tel" value={loginPhone} onChange={(e) => setLoginPhone(e.target.value)} required placeholder="05xxxxxxxx" dir="ltr" />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>كلمة المرور</Label>
                    <Input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required placeholder="••••••••" dir="ltr" />
                  </div>
                  <Button type="submit" className="w-full gradient-hero border-0" disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "تسجيل الدخول"}
                  </Button>
                </form>
              </TabsContent>

              {/* SIGNUP TAB */}
              <TabsContent value="signup">
                {/* Role selector */}
                <div className="flex gap-2 mb-5">
                  <Button
                    type="button"
                    variant={signupType === "admin" ? "default" : "outline"}
                    className="flex-1 gap-2"
                    onClick={() => setSignupType("admin")}
                  >
                    <School className="w-4 h-4" />
                    إدارة حضانة
                  </Button>
                  <Button
                    type="button"
                    variant={signupType === "parent" ? "default" : "outline"}
                    className="flex-1 gap-2"
                    onClick={() => setSignupType("parent")}
                  >
                    <UserRound className="w-4 h-4" />
                    ولي أمر
                  </Button>
                </div>

                <form onSubmit={signupType === "admin" ? handleAdminSignup : handleParentSignup} className="space-y-4">
                  <div className="space-y-2">
                    <Label>الاسم الكامل</Label>
                    <Input value={fullName} onChange={(e) => setFullName(e.target.value)} required placeholder="محمد أحمد" />
                  </div>

                  {signupType === "admin" ? (
                    <>
                      <div className="space-y-2">
                        <Label>اسم الحضانة *</Label>
                        <Input value={nurseryName} onChange={(e) => setNurseryName(e.target.value)} required placeholder="حضانة النور" />
                      </div>
                      <div className="space-y-2">
                        <Label>عنوان الحضانة *</Label>
                        <Input value={nurseryAddress} onChange={(e) => setNurseryAddress(e.target.value)} required placeholder="المدينة - الشارع" />
                      </div>
                      <div className="space-y-2">
                        <Label>هاتف الحضانة *</Label>
                        <Input value={nurseryPhone} onChange={(e) => setNurseryPhone(e.target.value)} required placeholder="0xxxxxxxxx" dir="ltr" className="text-right" />
                      </div>
                      <div className="space-y-2">
                        <Label>الرقم الضريبي (اختياري)</Label>
                        <Input value={nurseryTaxNumber} onChange={(e) => setNurseryTaxNumber(e.target.value)} placeholder="123-456-789" dir="ltr" className="text-right" />
                      </div>

                      {/* Plan Selection */}
                      <div className="space-y-2">
                        <Label>اختر الخطة</Label>
                        <div className="grid gap-2">
                          {plans.map((plan) => {
                            const isSelected = selectedPlan === plan.id;
                            const PlanIcon = plan.id === "basic" ? Shield : plan.id === "standard" ? GraduationCap : Crown;
                            return (
                              <button
                                key={plan.id}
                                type="button"
                                onClick={() => setSelectedPlan(plan.id)}
                                className={`relative flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-right ${
                                  isSelected
                                    ? "border-primary bg-primary/5"
                                    : "border-border hover:border-muted-foreground/30"
                                }`}
                              >
                                <PlanIcon className={`w-5 h-5 shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm">{plan.name}</span>
                                    {plan.popular && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">الأكثر طلباً</Badge>}
                                  </div>
                                  <p className="text-xs text-muted-foreground">{plan.monthlyPrice} ج.م / شهرياً</p>
                                </div>
                                <div className={`w-4 h-4 rounded-full border-2 shrink-0 ${isSelected ? "border-primary bg-primary" : "border-muted-foreground/40"}`}>
                                  {isSelected && <div className="w-1.5 h-1.5 bg-primary-foreground rounded-full m-auto mt-[3px]" />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Free Trial Toggle */}
                      <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                        <div className="flex items-center gap-2">
                          <Gift className="w-4 h-4 text-primary" />
                          <div>
                            <p className="text-sm font-medium">تجربة مجانية 15 يوم</p>
                            <p className="text-xs text-muted-foreground">ابدأ مجاناً بدون أي رسوم</p>
                          </div>
                        </div>
                        <Switch checked={wantTrial} onCheckedChange={setWantTrial} aria-label="تفعيل التجربة المجانية لمدة 15 يوم" />
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <Label>اختر الحضانة</Label>
                      {loadingNurseries ? (
                        <div className="flex items-center justify-center py-3">
                          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        </div>
                      ) : nurseries.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-3">لا توجد حضانات مسجلة بعد</p>
                      ) : (
                        <>
                          <div className="relative mb-2">
                            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="ابحث عن حضانة..."
                              value={nurserySearch}
                              onChange={(e) => setNurserySearch(e.target.value)}
                              className="pr-9"
                            />
                          </div>
                          <Select value={selectedNurseryId} onValueChange={setSelectedNurseryId}>
                            <SelectTrigger>
                              <SelectValue placeholder="اختر الحضانة" />
                            </SelectTrigger>
                            <SelectContent>
                              {filteredNurseries.map((n) => (
                                <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>
                              ))}
                              {filteredNurseries.length === 0 && (
                                <div className="px-3 py-2 text-sm text-muted-foreground text-center">لا توجد نتائج</div>
                              )}
                            </SelectContent>
                          </Select>
                        </>
                      )}
                    </div>
                  )}

                  {/* Signup method toggle */}
                  <div>
                    <Label className="mb-2 block">طريقة تسجيل الدخول</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={signupMethod === "email" ? "default" : "outline"}
                        className="flex-1 gap-2"
                        onClick={() => setSignupMethod("email")}
                      >
                        <Mail className="w-4 h-4" />
                        بريد إلكتروني
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={signupMethod === "phone" ? "default" : "outline"}
                        className="flex-1 gap-2"
                        onClick={() => setSignupMethod("phone")}
                      >
                        <Phone className="w-4 h-4" />
                        رقم الهاتف
                      </Button>
                    </div>
                  </div>

                  {signupMethod === "email" ? (
                    <div className="space-y-2">
                      <Label>البريد الإلكتروني</Label>
                      <Input type="email" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} required placeholder="email@example.com" dir="ltr" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>رقم الهاتف</Label>
                      <Input type="tel" value={signupPhone} onChange={(e) => setSignupPhone(e.target.value)} required placeholder="05xxxxxxxx" dir="ltr" />
                      <p className="text-xs text-muted-foreground">ستستخدم رقم الهاتف وكلمة المرور لتسجيل الدخول</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>كلمة المرور</Label>
                    <Input type="password" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} required minLength={6} placeholder="••••••••" dir="ltr" />
                  </div>
                  <Button type="submit" className="w-full gradient-hero border-0" disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : signupType === "admin" ? "إنشاء حساب وحضانة" : "تسجيل كولي أمر"}
                  </Button>
                </form>
              </TabsContent>
            </CardContent>
          </Tabs>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Auth;
